import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Accordion, Badge, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { personsTeamsSummaryGet, personTeamsUpdate, teamCreate, teamsGetAll, teamUpdate } from '../api/index.js'

import { Button } from '../common/Button.tsx'
import { IconAddNew2 } from '../common/icons.jsx'
import { PersonSelect2 } from '../common/personselect2.jsx'
import Switch from '../common/Switch.jsx'

const emptyTeam = {
	id: null,
	name: '',
	color: '#003a78',
	leader: null,
	code: '',
	active: true,
}

export function TeamManagementView() {
	const { t } = useTranslation()
	const gridRef = useRef()

	const [teams, setTeams] = useState([])
	const [dropdownTeams, setDropdownTeams] = useState([])
	const [personRows, setPersonRows] = useState([])
	const [showValidation, setShowValidation] = useState(false)

	const today = () => new Date().toISOString().slice(0, 10)

	const mapTeamToApi = (team) => ({
		id: team.id || 0,
		teamCode: team.code,
		teamName: team.name,
		leaderPersonId: team.leader || null,
		active: team.active,
		color: team.color,
		startDate: today(),
	})

	const loadTeams = useCallback(async () => {
		try {
			const res = await teamsGetAll()
			const data = res.data || []

			const mapped = data.map((t) => ({
				id: t.id,
				name: t.teamName,
				color: t.color || '#cccccc',
				leader: t.leaderPersonId,
				code: t.teamCode,
				active: t.active,
			}))

			setTeams(mapped.length ? mapped : [{ ...emptyTeam }])
			setDropdownTeams(
				mapped.map((t) => ({
					id: t.id,
					teamName: t.name,
					teamColor: t.color,
					active: t.active,
				})),
			)
		} catch (error) {
			console.error('Virhe tiimien latauksessa:', error)
		}
	}, [])

	const loadSummary = useCallback(async () => {
		try {
			const res = await personsTeamsSummaryGet()
			const rows = res.data || []

			setPersonRows(
				rows.map((r) => ({
					personId: r.personId,
					firstName: r.personName,
					lastName: '',
					selectedTeams: (r.assignedTeams || []).map((t) => t.teamId),
				})),
			)
		} catch (error) {
			console.error('Virhe tiimiyhteenvedon latauksessa:', error)
		}
	}, [])

	useEffect(() => {
		loadTeams()
		loadSummary()
	}, [loadTeams, loadSummary])

	const saveTeams = async () => {
		//const validTeams = teams.filter((team) => team.name?.trim() || team.code?.trim() || team.leader)

		const currentTeams = [...teams]

		try {
			for (let i = 0; i < currentTeams.length; i++) {
				const team = currentTeams[i]
				if (!team.name?.trim() || !team.code?.trim()) continue

				const payload = mapTeamToApi(team)

				if (team.id) {
					await teamUpdate(team.id, payload)
				} else {
					const savedTeam = await teamCreate(payload)
					currentTeams[i] = { ...team, id: savedTeam.data.id }
				}
			}

			setTeams(currentTeams)
			await loadTeams()
			await loadSummary()
		} catch (error) {
			console.error('Virhe tiimien tallennuksessa:', error)
		}
	}

	const addRow = () => setTeams([...teams, { ...emptyTeam }])

	const updateTeam = (index, field, value) => {
		setTeams((prevTeams) =>
			prevTeams.map((team, i) => {
				if (i === index) {
					return { ...team, [field]: value }
				}
				return team
			}),
		)
	}

	const onCellValueChanged = async (params) => {
		if (params.colDef.field !== 'selectedTeams') return

		const personId = params.data.personId
		const newIds = params.data.selectedTeams || []
		const oldIds = params.oldValue || []

		if (JSON.stringify(newIds) === JSON.stringify(oldIds)) return

		try {
			const assignments = newIds.map((id) => ({
				teamId: id,
				startDate: today(),
				endDate: null,
			}))

			await personTeamsUpdate(personId, assignments)
		} catch (error) {
			console.error('Virhe henkilön tiimin päivityksessä:', error)
			await loadSummary()
		}
	}

	const columnDefs = [
		{
			headerName: t('Person', 'Henkilö'),
			valueGetter: (p) => `${p.data.firstName} ${p.data.lastName}`,
			sortable: true,
			width: 220,
		},
		{
			headerName: t('Teams', 'Tiimit'),
			field: 'selectedTeams',
			editable: true,
			singleClickEdit: true,
			width: 360,
			cellEditor: 'agRichSelectCellEditor',
			cellEditorParams: {
				values: dropdownTeams.map((t) => t.id),
				multiSelect: true,
				formatValue: (value) => dropdownTeams.find((t) => t.id === value)?.teamName || value,
			},
			cellRenderer: (params) => {
				const teamsValues = params.value
				if (!teamsValues?.length) {
					return <span style={{ color: '#888' }}>{t('SelectTeam', 'Valitse tiimi')}</span>
				}

				return (
					<>
						{teamsValues.map((id) => {
							const team = dropdownTeams.find((t) => t.id === id)
							if (!team) return null

							const bgColor = team.active ? team.teamColor : '#888'

							return (
								<Badge
									key={id}
									bg={bgColor}
									style={{
										backgroundColor: bgColor,
										color: '#fff',
										marginRight: 4,
									}}
								>
									{team.teamName}
								</Badge>
							)
						})}
					</>
				)
			},
		},
	]

	return (
		<div className="d-flex flex-column" style={{ height: '100%', background: '#FFF' }}>
			<Accordion defaultActiveKey="teams">
				<Accordion.Item eventKey="teams">
					<Accordion.Header>{t('Teams', 'Tiimit')}</Accordion.Header>
					<Accordion.Body>
						{teams.map((team, index) => (
							<Row key={team.id || `new-team-${index}`} className="align-items-center mb-2">
								<Col className="mt-4" style={{ flex: '0 0 50px', maxWidth: 50 }}>
									<Form.Control
										type="color"
										value={team.color}
										onChange={(e) => updateTeam(index, 'color', e.target.value)}
										style={{ width: 36, height: 36, padding: 0 }}
									/>
								</Col>

								<Col md={3}>
									<Form.Label
										style={{
											position: 'relative',
											left: '.8rem',
											top: '.4rem',
											zIndex: 1,
											margin: 0,
											background: 'var(--almost-white)',
											color: 'var(--label-color)',
											fontSize: '11px',
										}}
									>
										{t('Name', 'Nimi')}
									</Form.Label>
									<Form.Control
										value={team.name}
										onChange={(e) => updateTeam(index, 'name', e.target.value)}
										isInvalid={showValidation && !team.name?.trim()}
									/>
									{showValidation && !team.name?.trim() && (
										<div className="text-danger" style={{ fontSize: '0.8rem' }}>
											{t('NameIsMandatory', 'Nimi on pakollinen')}
										</div>
									)}
								</Col>

								<Col md={3}>
									<Form.Label
										style={{
											position: 'relative',
											left: '.8rem',
											top: '.4rem',
											zIndex: 1,
											margin: 0,
											background: 'var(--almost-white)',
											color: 'var(--label-color)',
											fontSize: '11px',
										}}
									>
										{t('Leader', 'Vetäjä')}
									</Form.Label>
									<PersonSelect2
										field="leader"
										data={team}
										selectChange={(val) => {
											updateTeam(index, 'leader', val.value)
										}}
									/>
								</Col>
								<Col md={3}>
									<Form.Label
										style={{
											position: 'relative',
											left: '.8rem',
											top: '.4rem',
											zIndex: 1,
											margin: 0,
											background: 'var(--almost-white)',
											color: 'var(--label-color)',
											fontSize: '11px',
										}}
									>
										{t('Code', 'Tunniste')}
									</Form.Label>
									<Form.Control
										value={team.code}
										onChange={(e) => updateTeam(index, 'code', e.target.value)}
										isInvalid={showValidation && !team.code?.trim()}
									/>
									{showValidation && !team.code?.trim() && (
										<div className="text-danger" style={{ fontSize: '0.8rem' }}>
											{t('CodeIsMandatory', 'Tunnus on pakollinen')}
										</div>
									)}
								</Col>
								<Col md={1} className="d-flex align-items-center mt-3">
									<Switch
										className="d-flex align-items-center"
										checked={team.active}
										onChange={(e) => updateTeam(index, 'active', e.target.checked)}
									/>
									<span
										style={{
											marginLeft: '6px',
											fontSize: '0.8rem',
											whiteSpace: 'nowrap',
										}}
									>
										{team.active ? t('Active', 'Aktiivinen') : t('Activate', 'Aktivoi')}
									</span>
								</Col>
							</Row>
						))}

						<div className="d-flex mt-4">
							<Button variant="outline-primary" className="d-flex align-items-center gap-2 me-auto" onClick={addRow}>
								<IconAddNew2 />
								{t('AddTeam', 'Lisää uusi tiimi')}
							</Button>
							<Button
								className="me-2"
								onClick={() => {
									const validTeams = teams.filter((team) => team.name?.trim() || team.code?.trim() || team.leader)

									if (validTeams.some((t) => !t.name?.trim() || !t.code?.trim())) {
										setShowValidation(true)
										return
									}

									setShowValidation(false)
									saveTeams()
								}}
							>
								{t('Save', 'Tallenna')}
							</Button>
						</div>
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>

			<div className="ag-theme-material mt-3" style={{ flex: 1 }}>
				<AgGridReact
					ref={gridRef}
					rowData={personRows}
					columnDefs={columnDefs}
					animateRows
					suppressCellFocus
					onCellValueChanged={onCellValueChanged}
					stopEditingWhenCellsLoseFocus
					getRowId={(p) => p.data.personId.toString()}
				/>
			</div>
		</div>
	)
}
